import { useState } from 'react';
import type { TableProps } from '@cloudscape-design/components';

/**
 * Click-to-sort headers for a Cloudscape Table whose items are all in memory. Give each sortable
 * column a `sortingField` (its identity, so the header arrow survives re-renders that rebuild the
 * column objects) and an ascending `sortingComparator`, then spread the result onto the Table.
 * Ties keep the items' incoming order, so the server's ordering is the tie-break.
 */
export function useTableSort<T>(
  items: readonly T[],
  columns: readonly TableProps.ColumnDefinition<T>[],
  initial?: { field: string; descending?: boolean },
) {
  const [field, setField] = useState<string | undefined>(initial?.field);
  const [descending, setDescending] = useState(initial?.descending ?? false);

  const column = columns.find((c) => c.sortingField !== undefined && c.sortingField === field);
  const compare = column?.sortingComparator;
  // Columns are rebuilt every render, so there is nothing stable to memoize on; the lists are small.
  const sorted = compare ? [...items].sort((a, b) => (descending ? -1 : 1) * compare(a, b)) : [...items];

  return {
    items: sorted,
    columnDefinitions: columns as TableProps.ColumnDefinition<T>[],
    ...(column ? { sortingColumn: column } : {}),
    sortingDescending: descending,
    onSortingChange: ({ detail }: { detail: TableProps.SortingState<T> }) => {
      setField(detail.sortingColumn.sortingField);
      setDescending(detail.isDescending ?? false);
    },
  };
}

export const byNumber = <T>(get: (x: T) => number) => (a: T, b: T) => get(a) - get(b);
export const byText = <T>(get: (x: T) => string) => (a: T, b: T) => get(a).localeCompare(get(b), 'en-US', { sensitivity: 'base' });
